import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Layers, Plus, RefreshCw, Save, Trash2, ChevronLeft } from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../services/api";

/**
 * AdminJobs (V3.14) — Gestion du référentiel Métiers & Catégories.
 * Réservé au Super Administrateur (create/update/delete).
 * Toutes les valeurs sont extensibles — rien n'est codé en dur.
 */
export default function AdminJobs() {
  const { user } = useAuth();
  const isAdmin = ["admin", "super-admin"].includes(user?.role);
  const [categories, setCategories] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingCat, setSavingCat] = useState(false);
  const [newCat, setNewCat] = useState({ code: "", name: "", description: "", color: "#009640", icon: "layers", order: 99 });
  const [savingJob, setSavingJob] = useState(false);
  const [newJob, setNewJob] = useState({ name: "", category_code: "" });
  const [backfilling, setBackfilling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, j] = await Promise.all([
        api.get("/v1/job-categories?include_inactive=true"),
        api.get("/v1/jobs?include_inactive=true&limit=500"),
      ]);
      setCategories(c.data.items || []);
      setJobs(j.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addCategory = async () => {
    if (!newCat.code.trim() || !newCat.name.trim()) {
      return toast.error("Code et nom obligatoires");
    }
    setSavingCat(true);
    try {
      await api.post("/v1/admin/job-categories", { ...newCat, code: newCat.code.toUpperCase() });
      toast.success("Catégorie créée");
      setNewCat({ code: "", name: "", description: "", color: "#009640", icon: "layers", order: 99 });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingCat(false); }
  };

  const patchCategory = async (id, patch) => {
    try {
      await api.patch(`/v1/admin/job-categories/${id}`, patch);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("Désactiver cette catégorie ? Les métiers rattachés seront aussi désactivés.")) return;
    try {
      await api.delete(`/v1/admin/job-categories/${id}`);
      toast.success("Catégorie désactivée");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addJob = async () => {
    if (!newJob.name.trim() || !newJob.category_code) {
      return toast.error("Nom et catégorie obligatoires");
    }
    setSavingJob(true);
    try {
      await api.post("/v1/admin/jobs", newJob);
      toast.success("Métier créé");
      setNewJob({ name: "", category_code: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingJob(false); }
  };

  const patchJob = async (id, patch) => {
    try {
      await api.patch(`/v1/admin/jobs/${id}`, patch);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteJob = async (id) => {
    if (!window.confirm("Désactiver ce métier ?")) return;
    try {
      await api.delete(`/v1/admin/jobs/${id}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/v1/admin/jobs/backfill");
      toast.success(`Backfill OK : ${JSON.stringify(data.backfill)}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBackfilling(false); }
  };

  if (!isAdmin) {
    return (
      <PrivateLayout title="Référentiel Métiers">
        <div className="afr-empty" data-testid="jobs-forbidden">
          <div className="afr-empty__title">Réservé aux administrateurs</div>
        </div>
      </PrivateLayout>
    );
  }

  return (
    <PrivateLayout
      title="🏷️ Référentiel Métiers & Catégories"
      breadcrumbs={[{ to: "/admin", label: "Admin" }, { label: "Métiers" }]}
      actions={
        <div style={{ display: "flex", gap: 6 }}>
          <button className="afr-btn afr-btn--outline afr-btn--sm" onClick={load} data-testid="admin-jobs-refresh">
            <RefreshCw size={12} /> Actualiser
          </button>
          <button className="afr-btn afr-btn--primary afr-btn--sm" onClick={runBackfill} disabled={backfilling} data-testid="admin-jobs-backfill">
            {backfilling ? "…" : "Backfill"}
          </button>
          <Link to="/admin" className="afr-btn afr-btn--outline afr-btn--sm"><ChevronLeft size={14} /> Admin</Link>
        </div>
      }
    >
      <div data-testid="admin-jobs-page">
        {loading && <div>Chargement…</div>}

        {/* ═══ CATÉGORIES ═══ */}
        <section className="afr-card" style={{ padding: 14, marginBottom: 16 }} data-testid="admin-jobs-categories-card">
          <h3 style={{ margin: "0 0 10px", fontSize: "0.95rem" }}>Catégories</h3>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
            <thead>
              <tr style={{ background: "rgba(15,42,66,0.04)" }}>
                <th style={th}>Code</th>
                <th style={th}>Nom</th>
                <th style={th}>Description</th>
                <th style={th}>Couleur</th>
                <th style={th}>Ordre</th>
                <th style={th}>Actif</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((c) => (
                <tr key={c.id} data-testid={`cat-row-${c.code}`}>
                  <td style={td}><strong>{c.code}</strong></td>
                  <td style={td}>{c.name}</td>
                  <td style={{ ...td, color: "#64748b" }}>{c.description}</td>
                  <td style={td}><span style={{ display: "inline-block", width: 16, height: 16, background: c.color || "#000", borderRadius: 4 }} /></td>
                  <td style={td}>{c.order}</td>
                  <td style={td}>{c.active ? "✅" : "❌"}</td>
                  <td style={td}>
                    <button className="afr-btn afr-btn--ghost afr-btn--sm" onClick={() => patchCategory(c.id, { active: !c.active })} data-testid={`cat-toggle-${c.code}`}>
                      {c.active ? "Désactiver" : "Réactiver"}
                    </button>
                    {c.active && (
                      <button className="afr-btn afr-btn--ghost afr-btn--sm" onClick={() => deleteCategory(c.id)} data-testid={`cat-delete-${c.code}`} style={{ color: "#B45309", marginLeft: 4 }}>
                        <Trash2 size={11} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {/* Nouvelle catégorie */}
              <tr>
                <td style={td}><input value={newCat.code} onChange={(e) => setNewCat((s) => ({ ...s, code: e.target.value }))} placeholder="Ex : SANTE" data-testid="cat-new-code" style={inp} /></td>
                <td style={td}><input value={newCat.name} onChange={(e) => setNewCat((s) => ({ ...s, name: e.target.value }))} placeholder="Santé" data-testid="cat-new-name" style={inp} /></td>
                <td style={td}><input value={newCat.description} onChange={(e) => setNewCat((s) => ({ ...s, description: e.target.value }))} placeholder="Description" data-testid="cat-new-desc" style={inp} /></td>
                <td style={td}><input type="color" value={newCat.color} onChange={(e) => setNewCat((s) => ({ ...s, color: e.target.value }))} data-testid="cat-new-color" /></td>
                <td style={td}><input type="number" value={newCat.order} onChange={(e) => setNewCat((s) => ({ ...s, order: Number(e.target.value) }))} data-testid="cat-new-order" style={{ ...inp, width: 60 }} /></td>
                <td style={td}>—</td>
                <td style={td}>
                  <button className="afr-btn afr-btn--primary afr-btn--sm" onClick={addCategory} disabled={savingCat} data-testid="cat-new-submit">
                    <Plus size={12} /> Créer
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* ═══ MÉTIERS ═══ */}
        <section className="afr-card" style={{ padding: 14 }} data-testid="admin-jobs-jobs-card">
          <h3 style={{ margin: "0 0 10px", fontSize: "0.95rem" }}>Métiers ({jobs.length})</h3>
          <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
            <input value={newJob.name} onChange={(e) => setNewJob((s) => ({ ...s, name: e.target.value }))} placeholder="Nom du métier" data-testid="job-new-name" style={{ ...inp, flex: "1 1 200px" }} />
            <select value={newJob.category_code} onChange={(e) => setNewJob((s) => ({ ...s, category_code: e.target.value }))} data-testid="job-new-cat" style={inp}>
              <option value="">— Catégorie —</option>
              {categories.filter((c) => c.active).map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
            <button className="afr-btn afr-btn--primary afr-btn--sm" onClick={addJob} disabled={savingJob} data-testid="job-new-submit">
              <Plus size={12} /> Créer
            </button>
          </div>
          <div style={{ maxHeight: 460, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
              <thead>
                <tr style={{ background: "rgba(15,42,66,0.04)", position: "sticky", top: 0 }}>
                  <th style={th}>Métier</th>
                  <th style={th}>Catégorie</th>
                  <th style={th}>Actif</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id} data-testid={`job-row-${j.id}`}>
                    <td style={td}>{j.name}</td>
                    <td style={td}>{j.category_code}</td>
                    <td style={td}>{j.active ? "✅" : "❌"}</td>
                    <td style={td}>
                      <button className="afr-btn afr-btn--ghost afr-btn--sm" onClick={() => patchJob(j.id, { active: !j.active })} data-testid={`job-toggle-${j.id}`}>
                        {j.active ? "Désactiver" : "Réactiver"}
                      </button>
                      {j.active && (
                        <button className="afr-btn afr-btn--ghost afr-btn--sm" onClick={() => deleteJob(j.id)} data-testid={`job-delete-${j.id}`} style={{ color: "#B45309", marginLeft: 4 }}>
                          <Trash2 size={11} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </PrivateLayout>
  );
}

const th = { textAlign: "left", padding: "6px 8px", fontWeight: 600, fontSize: "0.78rem" };
const td = { padding: "6px 8px", borderBottom: "1px solid var(--afr-border,#E2E8F0)" };
const inp = { padding: "5px 8px", border: "1px solid var(--afr-border,#E2E8F0)", borderRadius: 4, fontSize: "0.82rem" };
